'use client';

import { Badge } from '@ahafood/ui/components/badge';
import { Image } from '@ahafood/ui/components/image';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, formatCurrency } from '@ahm/common-helpers';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SoloMeal } from '@/api/use-get-menu-ws';

interface SoloMealCarouselProps {
  soloMeals: SoloMeal[];
  currentItemId: number;
  onItemClick: (soloMeal: SoloMeal) => void;
  isLoading: boolean;
}

export function SoloMealCarousel({
  soloMeals,
  currentItemId,
  onItemClick,
  isLoading,
}: SoloMealCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows, soloMeals.length]);

  const scrollByDir = (dir: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-neutral-90 mb-3">
          Món SoloMeal khác
        </h3>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex-none w-32">
              <Skeleton className="aspect-square w-full rounded-2xl mb-2" />
              <Skeleton className="h-4 w-full mb-1" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (soloMeals.length === 0) {
    return null;
  }

  return (
    <div className="relative mb-4 -mx-4 w-screen bg-blue-10 md:mx-0 md:w-full md:min-w-0 md:max-w-full md:rounded-xl">
      {/* Native horizontal scroll (touch / trackpad) + desktop arrow controls.
          overflow-x-auto keeps min-content at 0 so the dialog (CSS grid) can't
          blow out its max-width. */}
      <div
        ref={scrollRef}
        onScroll={updateArrows}
        className="flex gap-3 overflow-x-auto scroll-smooth p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {soloMeals.map((soloMeal) => {
          const isActive = soloMeal._id === currentItemId;
          const imageSrc =
            soloMeal.image_preview_url && soloMeal.image_preview_url !== ''
              ? soloMeal.image_preview_url
              : soloMeal.image_thumb_url ||
                `${assetPrefix}/static/images/empty-store-bot.webp`;

          return (
            <div key={soloMeal._id} className="relative shrink-0">
              {isActive && (
                <Badge className="z-50 absolute top-0 left-0 rounded-none rounded-br rounded-tl bg-blue-40 px-1.5 py-0 text-[10px] font-semibold leading-5 text-white">
                  Đang chọn
                </Badge>
              )}
              <button
                type="button"
                onClick={() => onItemClick(soloMeal)}
                className={cn(
                  isActive ? 'border-blue-40' : 'border-none',
                  'flex gap-3 border-2 rounded-lg p-2 w-52 bg-white flex-shrink-0 h-fit'
                )}
                disabled={isActive}
              >
                <div className="relative size-12 aspect-square shrink-0">
                  <img
                    src={imageSrc}
                    alt={soloMeal.name}
                    className="size-full object-cover rounded-md"
                  />
                  {soloMeal.is_ai_image && (
                    <Badge className="absolute top-1 right-1 size-6 bg-white/50 hover:bg-white/90 text-white border-none shadow-sm p-1">
                      <Image
                        fill
                        className="p-0.5"
                        src={`${assetPrefix}/static/icons/white-ai.svg`}
                        alt={'AI'}
                      />
                    </Badge>
                  )}
                </div>
                <div className="flex flex-col gap-0 flex-1 min-w-0 h-fit">
                  <span className="font-normal text-base text-neutral-90 truncate text-left">
                    {soloMeal.name}
                  </span>

                  {soloMeal.discounted_price !== soloMeal.price ? (
                    <div className="text-right">
                      <span className="line-through truncate text-xs text-foreground">
                        {formatCurrency(soloMeal.price)}
                      </span>
                      <span className="text-primary text-sm font-semibold ml-1">
                        {' '}
                        {formatCurrency(soloMeal.discounted_price)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-primary text-sm font-semibold ml-1 text-right">
                      {formatCurrency(soloMeal.discounted_price)}
                    </span>
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* Desktop navigation arrows — only when there's room to scroll */}
      {canLeft && (
        <button
          type="button"
          aria-label="Trước"
          onClick={() => scrollByDir(-1)}
          className="hidden md:flex absolute left-1 top-1/2 z-10 size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-neutral-70 shadow-md ring-1 ring-black/5 transition hover:text-primary-50"
        >
          <ChevronLeft className="size-5" />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          aria-label="Sau"
          onClick={() => scrollByDir(1)}
          className="hidden md:flex absolute right-1 top-1/2 z-10 size-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-neutral-70 shadow-md ring-1 ring-black/5 transition hover:text-primary-50"
        >
          <ChevronRight className="size-5" />
        </button>
      )}
    </div>
  );
}
